import { FilmSlateIcon } from '@phosphor-icons/react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { buttonVariants } from '@/components/ui/button';

export interface ConfirmCostDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Heading, e.g. "Make a video". */
  title: string;
  /** Estimated cost line, e.g. "~$3.20 for an 8-second clip". */
  estimate: string;
  /** Optional secondary line (latency note etc.). */
  detail?: string;
  /** Confirm CTA label (default "Generate"). */
  confirmLabel?: string;
  onConfirm: () => void;
}

/**
 * Confirm-cost gate (§6.6). Video/animate generations are expensive (Veo
 * ~$0.40/sec → ~$3.20 for 8s) and slow, so they must NOT sit one-tap in the
 * chip row. This dialog surfaces the estimate and requires an explicit confirm
 * before the caller fires /api/generate-video.
 */
export function ConfirmCostDialog({
  open,
  onOpenChange,
  title,
  estimate,
  detail,
  confirmLabel = 'Generate',
  onConfirm,
}: ConfirmCostDialogProps): JSX.Element {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <FilmSlateIcon className="size-5 text-muted-foreground" weight="duotone" />
            {title}
          </AlertDialogTitle>
          <AlertDialogDescription className="space-y-1">
            <span className="block font-medium text-foreground tabular-nums">{estimate}</span>
            {detail ? <span className="block text-muted-foreground">{detail}</span> : null}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction className={buttonVariants({ variant: 'default' })} onClick={onConfirm}>
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
